import {useCallback, useEffect, useState} from 'react'
import {useNavigate} from 'react-router'
import PropTypes from 'prop-types'
import Dinero from 'dinero.js'
import {
  Accordion,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Link,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react'
import {Trans, useTranslation} from 'react-i18next'
import {Formik, Form} from 'formik'

import {PadlockIcon} from '~/react/icons'

import {PayablePropType} from '../../../propTypes'
import useFormSubmit from '../hooks/useFormSubmit'
import {PointsPanel} from './PointsPanel'
import PaymentDetailsForm from './PaymentDetailsForm'
import {calculateFees} from '../helpers/feeCalculator'
import PaymentMethodForm from './PaymentMethodForm'
import ReviewPanel from './ReviewPanel'
import IneligibleModal from './IneligibleModal'
import ConfirmPaymentModal from './ConfirmPaymentModal'

const FormPanel = ({payable, reloadPayable, isLoading, onSuccess}) => {
  const navigate = useNavigate()
  const {t} = useTranslation('pointsOnPayments')
  const {createPayment, paymentSubmitted, initialValues, validationSchema} =
    useFormSubmit(payable)

  const [expandedPanel, setExpandedPanel] = useState(0)
  const [showStaleVersionAlert, setShowStaleVersionAlert] = useState(false)
  const [isIneligibleModalOpen, setIsIneligibleModalOpen] = useState(false)
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const onCloseIneligibleModal = useCallback(() => {
    setIsIneligibleModalOpen(false)
    navigate('/points_on_payments/bills')
  }, [navigate])

  const i18nPrefix = 'pointsOnPayments:paymentWizard.formPanel'

  const onSubmit = useCallback(
    async (formValues) => {
      if (paymentSubmitted) return

      setShowStaleVersionAlert(false)

      const result = await createPayment({
        ...formValues,
        payableId: payable.id,
        payableVersion: payable.lockVersion,
        payeeVersion: payable.payee?.lockVersion,
      })

      setIsConfirmModalOpen(false)

      if (!result.success) {
        if (result.error.code === 'argument_error') {
          // TODO: show error message
          return
        }

        if (result.error.code === 'payable_ineligible') {
          setIsIneligibleModalOpen(true)
          return
        }

        if (result.error.code === 'stale_version') {
          setShowStaleVersionAlert(true)
          reloadPayable()
          return
        }

        return
      }

      onSuccess(result.data.payment.grossPaymentAmount.cents)
    },
    [
      createPayment,
      onSuccess,
      payable.id,
      payable.lockVersion,
      payable.payee?.lockVersion,
      paymentSubmitted,
      reloadPayable,
    ],
  )

  useEffect(() => {
    if (!payable) return

    if (!payable.canPay && payable.lifecycleStatus === 'eligible') {
      // If the status is eligible but something else is preventing the payable from being paid
      // Redirect to the bills page
      navigate('/points_on_payments/bills')
    }
  }, [payable, navigate])

  const totalAmount = useCallback(
    (amount) => {
      const parsedAmount = parseInt(amount, 10)

      if (!parsedAmount) {
        return null
      }

      const paymentAmount = Dinero({
        amount: parsedAmount,
      })

      return calculateFees({paymentAmount, offer: payable.offer}).totalAmount
    },
    [payable.offer],
  )

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          isSubmitting,
          isValid,
          setFieldValue,
          setFieldTouched,
          submitForm,
          ..._formikProps
        }) => (
          <Flex as={Form} direction="column" gap="md" overflow="visible">
            {showStaleVersionAlert && (
              <Alert status="warning">
                <AlertIcon />
                <Box>
                  <AlertTitle>
                    {t(`${i18nPrefix}.staleVersionAlert.title`)}
                  </AlertTitle>
                  <AlertDescription>
                    {t(`${i18nPrefix}.staleVersionAlert.description`)}
                  </AlertDescription>
                </Box>
              </Alert>
            )}
            <PointsPanel
              paymentAmount={errors.amount ? 0 : values.amount}
              isLoading={isLoading}
            />
            <Accordion
              defaultIndex={0}
              index={expandedPanel}
              onChange={setExpandedPanel}
              display="flex"
              flexDirection="column"
              gap="md"
            >
              <PaymentDetailsForm
                payable={payable}
                values={values}
                isValid={isValid}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                isLoading={isLoading}
              />
              <PaymentMethodForm
                payable={payable}
                values={values}
                isValid={!errors.amount && !errors.reference}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                isLoading={isLoading}
              />
              <ReviewPanel
                payable={payable}
                values={values}
                isValid={isValid}
                setFieldValue={setFieldValue}
              />
            </Accordion>

            <Text textStyle="body-copy">
              <Trans
                i18nKey={`${i18nPrefix}.termsAndConditions`}
                components={{
                  termsModalTrigger: (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <Link
                      variant="primary"
                      textStyle="body-copy"
                      isExternal
                      href="https://www.marmalade.com.au/marmalade-rewards-terms-and-conditions"
                    />
                  ),
                }}
              />
            </Text>
            <Box>
              <Button
                type="button"
                variant="primary"
                isLoading={isSubmitting}
                // Only allow the submit button to be clicked if the review step is expanded and the form is valid
                isDisabled={!isValid || expandedPanel !== 2}
                onClick={() => setIsConfirmModalOpen(true)}
              >
                {t(`${i18nPrefix}.submitButton`, {
                  context: values.amount ? 'withAmount' : 'withoutAmount',
                  amount: totalAmount(values.amount)?.toFormat(),
                })}
              </Button>
            </Box>
            <Box>
              <Tag bg="mld.neutral.200">
                <TagLeftIcon boxSize="16px" as={PadlockIcon} />
                <TagLabel textTransform="uppercase">
                  {t(`${i18nPrefix}.secureTag`)}
                </TagLabel>
              </Tag>
            </Box>
            <ConfirmPaymentModal
              isOpen={isConfirmModalOpen}
              onCancel={() => setIsConfirmModalOpen(false)}
              onConfirm={submitForm}
              payable={payable}
              totalAmount={totalAmount(values.amount)}
              isSubmitting={isSubmitting}
            />
          </Flex>
        )}
      </Formik>

      <IneligibleModal
        isOpen={isIneligibleModalOpen}
        onExit={onCloseIneligibleModal}
      />
    </>
  )
}

FormPanel.defaultProps = {
  payable: null,
  reloadPayable: () => {},
}

FormPanel.propTypes = {
  payable: PayablePropType,
  reloadPayable: PropTypes.func,
  isLoading: PropTypes.bool.isRequired,
  onSuccess: PropTypes.func.isRequired,
}

export default FormPanel
